import { Injectable, inject } from "@angular/core";
import {
  catchError,
  combineLatest,
  map,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from "rxjs";
import { VisionService } from "./vision.service";
import { AppConfigService } from "./app.config.service";
import { ConferenceService } from "./conference.service";
import { InteractionService } from "../../app/services/interaction.service";
import { LocaleService } from "../../app/services/locale.service";
import { NotificationService } from "./notification.service";
import { debug, delay, extractErrorMessage } from "./utils";
import { ConferenceStore, StreamState } from "./store/conference.store";
import { LocalMediaService } from "./local.media.service";
import {
  AILiveEvent,
  AIToolEvent,
  ConferenceMetadataKeyEnum,
} from "../core-ui.enums";
import { AIAssistantMetadata, CustomerParam } from "../models";
import { UserService } from "./user.service";
import {
  AssistantStore,
  AssistantTrackManagerService,
  ServerSyncState,
  StreamTrack,
} from "./assistant.track.manager.service";
import { AssistantAPIService } from "./assistant.api.service";

interface IAssistantLiveSession {
  id: string;
  greeted: boolean;
  localTrackState: AssistantStore;
  serverTrackState: AssistantStore;
  promptContext: string;
}

const DEFAULT_LIVE_SESSION: IAssistantLiveSession = {
  id: undefined,
  greeted: false,
  localTrackState: {},
  serverTrackState: {},
  promptContext: "",
};

@Injectable({
  providedIn: "root",
})
export class AssistantService {
  private config = inject(AppConfigService);
  private conferenceService = inject(ConferenceService);
  private interactionService = inject(InteractionService);
  private locale = inject(LocaleService);
  private notification = inject(NotificationService);
  private store = inject(ConferenceStore);
  private localMediaService = inject(LocalMediaService);
  private userService = inject(UserService);
  private trackManager = inject(AssistantTrackManagerService);
  private api = inject(AssistantAPIService);

  private _openChange: Subject<boolean> = new Subject();
  public openChange$ = this._openChange.asObservable();

  private _sessionReady = new Subject<IAssistantLiveSession>();
  public sessionReady$ = this._sessionReady.asObservable();

  private destroy$ = new Subject<void>();

  private session: IAssistantLiveSession;
  private subscription = new Subscription();
  private serverSyncRetries = 0;

  localTrackCount = 0;
  serverTrackCount = 0;
  syncStatus = "idle";

  private localStream: StreamState = undefined;
  private screenStream: StreamState = undefined;

  constructor() {
    const vision = inject(VisionService);
    const config = this.config;

    vision.imageStateChange$.subscribe((state) => {
      this._openChange.next(true);
    });

    config.configChanged$.subscribe((c) => {});

    combineLatest([this.trackManager.getLocalState(), this.sessionReady$])
      .pipe(
        map(([state]) => state),
        takeUntil(this.destroy$),
      )
      .subscribe((state) => {
        this.session.localTrackState = state;
      });

    // Listen to server state changes
    combineLatest([this.trackManager.getServerState(), this.sessionReady$])
      .pipe(
        map(([state]) => state),
        takeUntil(this.destroy$),
      )
      .subscribe((state) => {
        this.session.serverTrackState = state;
      });

    // Listen to sync state changes and automatically sync with server
    combineLatest([this.trackManager.getSyncState(), this.sessionReady$])
      .pipe(
        map(([syncState]) => syncState),
        takeUntil(this.destroy$),
        switchMap((syncState) => this.performServerSync(syncState)),
        catchError((error) => {
          console.error("Sync failed:", error);
          this.syncStatus = "error";
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.syncStatus = "synced";
        }
      });


    combineLatest([this.store.updated$, this.sessionReady$])
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {
        if (
          !!this.localMediaService.mainStream.stream &&
          this.store.mystream.published &&
          this.localStream !== this.store.mystream
        ) {
          this.localStream = this.store.mystream;
          this.trackManager.updateLocalStream(
            this.localMediaService.mainStream.stream,
            this.store.mystream,
            "camera",
          );
        }
        if (
          !!this.localMediaService.screenStream.stream &&
          this.store.screenStream.published &&
          this.screenStream !== this.store.screenStream
        ) {
          this.screenStream = this.store.screenStream;
          this.trackManager.updateLocalStream(
            this.localMediaService.screenStream.stream,
            this.store.screenStream,
            "screen",
          );
        }
      });

    combineLatest([
      this.conferenceService.localStreamPublished$,
      this.sessionReady$,
    ])
      .pipe(
        map(([stream]) => stream),
        takeUntil(this.destroy$),
      )
      .subscribe((stream) => {
        let tracks: StreamTrack[] = [];
        const { cameraAudio, cameraVideo, screenVideo } =
          this.session.localTrackState;
        const hasScreenVideo = !!screenVideo;

        // Toggle appropriate track
        switch (stream?.type) {
          case "screen":
            this.trackManager.toggleTrack("screen", "video", true);
            break;
          case "cam":
            this.trackManager.toggleTrack("camera", "video", true);
            break;
          case "video":
            this.trackManager.toggleTrack("camera", "video", true);
            this.trackManager.toggleTrack("camera", "audio", true);
            break;
          case "mic":
            this.trackManager.toggleTrack("camera", "audio", true);
            break;
        }

        // Build constraints based on stream type
        const requirements = {
          useCamera: {
            video:
              stream?.type === "cam" ||
              (stream?.type === "video" && !hasScreenVideo),
            audio:
              stream?.type === "mic" ||
              stream?.type === "video" ||
              !!cameraAudio,
          },
          ...(hasScreenVideo && { useScreen: { video: true, audio: false } }),
        };

        tracks = this.trackManager.getTracksForServer(requirements);
        this.trackManager.syncWithServer(tracks);
      });

    combineLatest([this.localMediaService.streamRemoved$, this.sessionReady$])
      .pipe(
        map(([stream]) => stream),
        takeUntil(this.destroy$),
      )
      .subscribe((stream) => {
        const { cameraAudio, cameraVideo, screenVideo } =
          this.session.localTrackState;

        let requirements = {};

        if (stream?.type === "screen") {
          this.trackManager.removeStreamBySource("screen");
          if (cameraVideo || cameraAudio) {
            requirements = {
              useCamera: { video: !!cameraVideo, audio: !!cameraAudio },
            };
          }
        } else if (["cam", "video", "mic"].includes(stream?.type)) {
          this.trackManager.removeStreamBySource("camera");
          if (screenVideo) {
            requirements = {
              useScreen: { video: true, audio: false },
            };
          }
        }

        const tracks = this.trackManager.getTracksForServer(requirements);
        this.trackManager.syncWithServer(tracks);
      });

    combineLatest([this.localMediaService.streamMutedChange$, this.sessionReady$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncTracksFromLocalState());
  }

  public toggleOpen(open: boolean) {
    this._openChange.next(open);
  }

  /**
   * Creates a session, joins it and when a stream is published, connect it with the AI service
   * @returns void
   */
  async initLiveSession() {
    if (!!this.session) {
      return;
    }

    // check if we have an existing session (maybe from a browser refresh)
    const conference = await this.conferenceService.getConferenceView(
      this.conferenceService.session.conference.name,
    );

    if (
      !!conference &&
      ConferenceMetadataKeyEnum.AIAssistant in conference.metadata
    ) {
      const existingMeta = AIAssistantMetadata.fromEvent(
        conference.metadata[ConferenceMetadataKeyEnum.AIAssistant],
      );

      this.session = {
        ...DEFAULT_LIVE_SESSION,
        id: existingMeta.sessionID,
        promptContext: existingMeta.promptContext,
      };
    }

    // we need to create and join to a session
    if (!this.session?.id) {
      await this.tryCreateSession();
      await this.tryJoinSession();
      this.storeSessionId();
    }
    // joinSession may fail, the room.ngOndestroy clears the session and then we get here
    if (this.session) this._sessionReady.next(this.session);
  }

  private storeSessionId() {
    if (!this.session) return;
    // save session id to conference metadata in case we refresh
    const assMeta = new AIAssistantMetadata(this.conferenceService.myself);
    assMeta.setSessionID(this.session.id);
    this.conferenceService.setConferenceMetadata(assMeta);
  }

  private async tryCreateSession() {
    try {
      const hasUsername = this.userService.getUserDetails()?.name;
      const { id } = await this.api.createSession({
        languageCode: this.locale.getLanguage(),
        username: hasUsername,
        endpoint: this.conferenceService.myself.endpoint,
        modality: "audio",
        promptContext: this.session?.promptContext,
      });
      this.session = {
        ...DEFAULT_LIVE_SESSION,
        ...this.session,
        id,
        greeted: !!hasUsername,
      };
    } catch (ex) {
      this.notification.error("Could not create AI session", {
        body: extractErrorMessage(ex),
      });
      this._sessionReady.error(ex);
      throw ex;
    }
  }

  private async tryJoinSession() {
    const maxRetries = 2;
    const retryInterval = 500; // milliseconds

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (!this.session) return;

        await this.api.joinSession(
          this.session.id,
          this.conferenceService.session.conference.name,
        );
        return; // Success, exit the function
      } catch (ex) {
        if (ex.response?.status === 404) {
          // session being processed - this might be worth retrying
          if (attempt < maxRetries) {
            debug(
              `Attempt ${
                attempt + 1
              } failed with 404, retrying in ${retryInterval}ms...`,
            );
            await delay(retryInterval);
            continue; // Retry for 404 errors
          }
        } else if (ex.response?.status === 400) {
          // Already joined - no need to retry
          return;
        }

        // For the last attempt or non-retryable errors, handle the error
        if (attempt === maxRetries || ex.response?.status !== 404) {
          this.notification.error("Could not join AI session", {
            body: extractErrorMessage(ex),
          });
          this._sessionReady.error(ex);
          throw ex;
        }
      }
    }
  }

  private async performServerSync(
    syncState: ServerSyncState,
  ): Promise<boolean> {
    try {
      if (!this.session?.greeted) {
        this.session.greeted = true;
      }

      // Remove tracks first (this handles both removal and "updates" by removing old state)
      if (syncState.tracksToRemove.length > 0) {
        debug("AI:Removing track(s):", syncState.tracksToRemove);
        await Promise.all(
          syncState.tracksToRemove.map((t) =>
            this.api.disconnectStreamTrack(this.session.id, t.kind, t.streamId),
          ),
        );
      }

      // Add new tracks (this handles both new tracks and "updates" by adding new state)
      if (syncState.tracksToAdd.length > 0) {
        debug("AI:Adding track(s):", syncState.tracksToAdd);
        await Promise.all(
          syncState.tracksToAdd.map((t) =>
            this.api.connectStreamTrack(this.session.id, t.kind, t.streamId),
          ),
        );
      }

      // Update server state - the server should now have exactly the tracks we just added
      // (since we removed the old ones first)
      this.trackManager.updateServerState(syncState);

      this.serverSyncRetries = 0; // reset retries on success

      return true;
    } catch (error) {
      if (error.response?.status == 404 && this.serverSyncRetries < 2) {
        // could not find session - maybe expired? try to create again
        this.session.id = undefined;

        await this.tryCreateSession();
        await this.tryJoinSession();
        this.storeSessionId();

        this.serverSyncRetries += 1;

        // after re-creating the session, we need to re-send all local tracks
        this.performServerSync(syncState);
      } else {
        throw error;
      }
    }
  }

  /**
   * Disconnects streams, removes session and unsubscribes from conference events
   * @returns void
   */
  async destroyLiveSession() {
    this.destroy$.next();
    this.destroy$.complete();

    this.subscription.unsubscribe();

    this.conferenceService.removeConferenceMetadata(
      ConferenceMetadataKeyEnum.AIAssistant,
    );

    if (!this.session) {
      return;
    }

    this.api.leaveSession(this.session.id);

    this.session = undefined;
  }

  public propagateEvent(event: {
    id: string;
    type: AILiveEvent | AIToolEvent;
  }) {
    switch (event.type) {
      case "AIToolEvent":
        debug("AI: event", event);
        break;
      case "AIToolCancellationEvent":
        debug("AI: event", event);
        break;
      case "AILiveCreatedEvent":
        debug("AI: event", event);
        break;
      case "AILiveEndedEvent":
        debug("AI: event", event);
        this.conferenceService.terminateConferenceRequest();
        break;
      case "AILiveFailedEvent":
        // if a stream fails, retry (send stream again)
        debug("AI: event", event);
        break;
      case "AILiveResponseEvent":
        debug("AI: event", event);
        break;
      case "AILiveStreamConnectedEvent":
        debug("AI: event", event);
        break;
    }
  }

  private syncTracksFromLocalState(): void {
    const { cameraAudio, cameraVideo, screenVideo } =
      this.session.localTrackState;
    const requirements = {
      ...(cameraVideo || cameraAudio
        ? { useCamera: { video: !!cameraVideo, audio: !!cameraAudio } }
        : {}),
      ...(screenVideo ? { useScreen: { video: true, audio: false } } : {}),
    };
    const tracks = this.trackManager.getTracksForServer(requirements);
    this.trackManager.syncWithServer(tracks);
  }

  /** getters */

  public hasActiveSession() {
    return !!this.session;
  }
}
